/**
 * NoSubscriptionBanner
 * --------------------
 * Persistent amber banner that surfaces on members who don't have an
 * active recurring Stripe Subscription. Owner spec 2026-02-17 (Option D):
 * "Don't let me forget. Nag me until I either subscribe them or change
 * their status."
 *
 * 2026-02-23 (Phase 2b cleanup): the banner now points the coach at the
 * "Subscribe from a pricing block" section instead of opening the legacy
 * StartSubscriptionButton modal.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NoSubscriptionBanner({ userId }) {
    const [status, setStatus] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    const load = useCallback(async () => {
        if (!userId) return;
        try {
            const r = await axios.get(
                `${API}/coach/clients/${userId}/subscription-status`,
                { withCredentials: true },
            );
            setStatus(r.data || null);
        } catch {
            setStatus(null);
        }
    }, [userId]);

    useEffect(() => {
        load();
    }, [load]);

    if (!status || dismissed) return null;
    if (status.user_status !== "member") return null;
    if (status.active) return null;

    const scrollToSubscribe = () => {
        const el = document.querySelector('[data-testid="lead-detail-subscribe-from-block"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div
            data-testid="no-sub-banner"
            className="border border-amber-700/60 bg-amber-900/20 px-4 py-3 mt-4 flex items-start gap-3"
        >
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
                <div className="text-sm text-white font-semibold">
                    Member has no recurring subscription
                </div>
                <div className="text-[12px] text-amber-200/90 mt-0.5 leading-snug">
                    This member is marked as a Member but no Stripe subscription is auto-billing them. Start one from a pricing block or they&apos;ll continue training without recurring payment.
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={scrollToSubscribe}
                        data-testid="no-sub-banner-start-now"
                        className="text-[10px] tracking-[0.2em] uppercase px-2.5 py-1.5 bg-amber-600 hover:bg-amber-500 text-black font-semibold"
                    >
                        Subscribe from a block ↓
                    </button>
                </div>
            </div>
            <button
                type="button"
                data-testid="no-sub-banner-dismiss"
                onClick={() => setDismissed(true)}
                className="text-amber-300/70 hover:text-white"
                aria-label="Dismiss for this session"
                title="Dismiss for this session (will reappear after refresh)"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}
